import React from "react";
import {
	Form, Input, DatePicker, Row, Col, Avatar,
} from "antd";
import {
	UserOutlined, MailOutlined, PhoneOutlined, HomeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { User } from "../../../types/admin/UserTypes";
import { TeamMember } from "../../../types/admin/TeamTypes";
import SectionCard from "../../shared/SectionCard";

interface PersonalTabProps {
	user: User | TeamMember;
	setUser: React.Dispatch<React.SetStateAction<any>>;
	mode: "admin" | "team";
	handleAutoSave: () => void;
}

const PersonalTab: React.FC<PersonalTabProps> = ({ user, setUser, mode, handleAutoSave }) => {
	const handleChange = (field: string, value: any) => {
		setUser((prev: any) => ({ ...prev, [field]: value }));
	};

	const handleEmergencyContactChange = (field: string, value: string) => {
		setUser((prev: any) => ({
			...prev,
			team_member_info: {
				...prev.team_member_info,
				emergency_contact: {
					...(prev.team_member_info?.emergency_contact || {}),
					[field]: value,
				},
			},
		}));
	};

	const teamMember = mode === "team" ? (user as TeamMember) : null;

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			{/* The picture route is self-only and ignores user_id, so staff get no upload or delete here (14ygtkjcvyx). */}
			{user.id !== "" && (
				<SectionCard title="Profile Picture">
					<Avatar
						size={96}
						src={user.profile_picture || "/assets/images/default-profile.png"}
						icon={<UserOutlined />}
						style={{ border: "2px solid var(--mg-border)" }}
					/>
				</SectionCard>
			)}

			{/* Personal Information */}
			<SectionCard title="Personal Information">
				<Form layout="vertical">
					<Row gutter={[16, 0]}>
						<Col xs={24} md={8}>
							<Form.Item label="First Name" required>
								<Input
									value={user.first_name || ""}
									onChange={(e) => handleChange("first_name", e.target.value)}
									onBlur={handleAutoSave}
									placeholder="Enter first name"
									prefix={<UserOutlined style={{ color: "var(--mg-text-disabled)" }} />}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Middle Name">
								<Input
									value={user.middle_name || ""}
									onChange={(e) => handleChange("middle_name", e.target.value)}
									onBlur={handleAutoSave}
									placeholder="Enter middle name"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Last Name" required>
								<Input
									value={user.last_name || ""}
									onChange={(e) => handleChange("last_name", e.target.value)}
									onBlur={handleAutoSave}
									placeholder="Enter last name"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Email" required>
								<Input
									type="email"
									value={user.email || ""}
									onChange={(e) => handleChange("email", e.target.value)}
									onBlur={handleAutoSave}
									placeholder="Enter email address"
									prefix={<MailOutlined style={{ color: "var(--mg-text-disabled)" }} />}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Username" required>
								<Input
									value={user.username || ""}
									onChange={(e) => handleChange("username", e.target.value)}
									onBlur={handleAutoSave}
									placeholder="Enter username"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Phone Number">
								<Input
									value={user.phone || ""}
									onChange={(e) => handleChange("phone", e.target.value)}
									onBlur={handleAutoSave}
									placeholder="Enter phone number"
									prefix={<PhoneOutlined style={{ color: "var(--mg-text-disabled)" }} />}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Date of Birth">
								<DatePicker
									value={user.date_of_birth ? dayjs(user.date_of_birth) : null}
									onChange={(date) => handleChange("date_of_birth", date ? date.toDate() : null)}
									onBlur={handleAutoSave}
									format="DD/MM/YYYY"
									style={{ width: "100%" }}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Gender">
								<Input
									value={user.gender || ""}
									onChange={(e) => handleChange("gender", e.target.value)}
									onBlur={handleAutoSave}
									placeholder="Enter gender"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Sex">
								<Input
									value={user.sex || ""}
									onChange={(e) => handleChange("sex", e.target.value)}
									onBlur={handleAutoSave}
									placeholder="Enter sex"
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</SectionCard>

			{/* Address */}
			<SectionCard title="Address">
				<Form layout="vertical">
					<Row gutter={[16, 0]}>
						<Col xs={24}>
							<Form.Item label="Street Address">
								<Input
									value={user.address || ""}
									onChange={(e) => handleChange("address", e.target.value)}
									onBlur={handleAutoSave}
									placeholder="Enter street address"
									prefix={<HomeOutlined style={{ color: "var(--mg-text-disabled)" }} />}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={6}>
							<Form.Item label="City">
								<Input
									value={user.city || ""}
									onChange={(e) => handleChange("city", e.target.value)}
									onBlur={handleAutoSave}
									placeholder="City"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={6}>
							<Form.Item label="State">
								<Input
									value={user.state || ""}
									onChange={(e) => handleChange("state", e.target.value)}
									onBlur={handleAutoSave}
									placeholder="State"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={6}>
							<Form.Item label="Country">
								<Input
									value={user.country || ""}
									onChange={(e) => handleChange("country", e.target.value)}
									onBlur={handleAutoSave}
									placeholder="Country"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={6}>
							<Form.Item label="Postal Code">
								<Input
									value={user.postal_code || ""}
									onChange={(e) => handleChange("postal_code", e.target.value)}
									onBlur={handleAutoSave}
									placeholder="Postal code"
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</SectionCard>

			{/* Emergency Contact — team mode only */}
			{mode === "team" && teamMember && (
				<SectionCard title="Emergency Contact">
					<Form layout="vertical">
						<Row gutter={[16, 0]}>
							<Col xs={24} md={8}>
								<Form.Item label="Name">
									<Input
										value={teamMember.team_member_info?.emergency_contact?.name || ""}
										onChange={(e) => handleEmergencyContactChange("name", e.target.value)}
										onBlur={handleAutoSave}
										placeholder="Emergency contact name"
										prefix={<UserOutlined style={{ color: "var(--mg-text-disabled)" }} />}
									/>
								</Form.Item>
							</Col>
							<Col xs={24} md={8}>
								<Form.Item label="Relationship">
									<Input
										value={teamMember.team_member_info?.emergency_contact?.relationship || ""}
										onChange={(e) => handleEmergencyContactChange("relationship", e.target.value)}
										onBlur={handleAutoSave}
										placeholder="Relationship to team member"
									/>
								</Form.Item>
							</Col>
							<Col xs={24} md={8}>
								<Form.Item label="Phone">
									<Input
										type="tel"
										value={teamMember.team_member_info?.emergency_contact?.phone || ""}
										onChange={(e) => handleEmergencyContactChange("phone", e.target.value)}
										onBlur={handleAutoSave}
										placeholder="Emergency contact phone"
										prefix={<PhoneOutlined style={{ color: "var(--mg-text-disabled)" }} />}
									/>
								</Form.Item>
							</Col>
						</Row>
					</Form>
				</SectionCard>
			)}
		</div>
	);
};

export default PersonalTab;
